import React, { useEffect, useRef, FC } from 'react';

import 'ol/ol.css';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import Draw, { createBox } from 'ol/interaction/Draw.js';
import Feature from 'ol/Feature';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { useTranslation } from 'react-i18next';
import { MAPBOX_STYLE_TILE } from '../../../features/predictionMap/constants/mapbox';
import Polygon from 'ol/geom/Polygon';
import { transform, fromLonLat } from 'ol/proj';
import { areaCoordinateType } from '../types';

type featureType = null | Feature;

type Props = {
  latLngOfCenter: [number, number] | null;
  setAreaCoordinates: React.Dispatch<React.SetStateAction<areaCoordinateType | null>>;
  setShouleRevGeoCode: React.Dispatch<React.SetStateAction<boolean>>;
};

const initialLatLon = [35.6764225, 139.650027] as [number, number];

const RectangularSelectMap: FC<Props> = ({
  setAreaCoordinates,
  latLngOfCenter,
  setShouleRevGeoCode,
}) => {
  const { i18n, t } = useTranslation();
  const { language } = i18n;

  const mapRef = useRef<Map | null>(null);
  const tileLayerRef = useRef<TileLayer<XYZ> | null>(null);
  const vectorSourceRef = useRef(new VectorSource());

  const mapStyleTile = React.useMemo(() => {
    const lang = language as 'ja' | 'en';
    return MAPBOX_STYLE_TILE[lang].default;
  }, [language]);

  useEffect(() => {
    const appliedLatLon = latLngOfCenter || initialLatLon;

    const lonLat = [appliedLatLon[1], appliedLatLon[0]];
    const center = fromLonLat(lonLat);

    // 既存のレイヤーの設定
    if (!mapRef.current) {
      // すでに地図が初期化されていない場合のみ実行
      tileLayerRef.current = new TileLayer<XYZ>({
        source: new XYZ({
          attributions: '© <a href="https://www.mapbox.com/map-feedback/">Mapbox</a>',
          url: mapStyleTile,
        }),
      });

      // ベクターレイヤーの追加（描画用）
      const vectorLayer = new VectorLayer({
        source: vectorSourceRef.current,
      });

      // 地図の初期化
      mapRef.current = new Map({
        layers: [tileLayerRef.current, vectorLayer],
        target: 'map',
        view: new View({
          center: center,
          zoom: 12,
        }),
        controls: [],
      });
    } else {
      mapRef.current.getView().setCenter(center);
    }
    if (tileLayerRef.current) {
      tileLayerRef.current.setSource(
        new XYZ({
          attributions: '© <a href="https://www.mapbox.com/map-feedback/">Mapbox</a>',
          url: mapStyleTile,
        }),
      );
    }

    let lastFeature: featureType = null;

    const draw = new Draw({
      source: vectorSourceRef.current,
      type: 'Circle',
      geometryFunction: createBox(),
      freehand: false,
    });
    draw.on('drawstart', () => {
      if (lastFeature) {
        vectorSourceRef.current.removeFeature(lastFeature);
      }
    });
    draw.on('drawend', event => {
      lastFeature = event.feature;
      const geometry = event.feature.getGeometry() as Polygon;
      if (geometry) {
        const coordinates = geometry.getCoordinates()[0];
        const bottomRight = transform(coordinates[1], 'EPSG:3857', 'EPSG:4326');
        const topLeft = transform(coordinates[3], 'EPSG:3857', 'EPSG:4326');
        setAreaCoordinates({
          topLeftLon: topLeft[0],
          topLeftLat: topLeft[1],
          bottomRightLon: bottomRight[0],
          bottomRightLat: bottomRight[1],
        });
        setShouleRevGeoCode(true);
      }
    });
    if (!!mapRef.current) {
      mapRef.current.addInteraction(draw);
      return;
    }
  }, [language, latLngOfCenter]);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ width: '100%', height: '55vh' }} id="map" />
    </div>
  );
};

export default RectangularSelectMap;
